// AST leaf collector that resolves nested selectors and at-rule chains.
// Example key: `@media...|.btn::0` for stable incremental tracking.
import type { LuisCompileWarning, LuisNode } from '../types'
import type { LuisRenderLeaf } from './types'
import { findInvalidObservedSelectorMessage } from './observed-selector-warning'
import { stripObservedMarkersFromSelector } from './observed-selector'
import { classifyObservedSelectorParts, registerObservedRootsFromSelector, type ObservedRootRegistry } from './observed-roots'
import { pushLeafGroups } from './leaf-groups'
import { failClosedSecurityCheck } from '../../security/guard'
import { hasUnsafeCssPayload } from '@lentystyle/guard/shared'

// Collect selector leaves from root AST in render order.
export function collectRenderLeaves(root: LuisNode, warnings?: LuisCompileWarning[]): LuisRenderLeaf[] {
  const leaves: LuisRenderLeaf[] = []
  const occurrenceByBaseKey = new Map<string, number>()
  const observedRoots = collectObservedRoots(root, warnings)

  // Collect leaf rules from each top-level child node.
  for (const child of root.children) {
    collectNodeLeaves(child, '', [], leaves, occurrenceByBaseKey, observedRoots, warnings)
  }

  // Return collected leaf list for rendering.
  return leaves
}

// Collect leaves from one node with inherited selector/at-rule context.
function collectNodeLeaves(
  node: LuisNode,
  parentSelector: string,
  atRules: string[],
  leaves: LuisRenderLeaf[],
  occurrenceByBaseKey: Map<string, number>,
  observedRoots: ObservedRootRegistry,
  warnings?: LuisCompileWarning[],
): void {
  // Pass through at-rule nodes and collect nested at-rule context.
  if (node.type === 'at-rule') {
    const nextAtRules = [...atRules, node.value]
    // Collect child leaves under updated at-rule chain.
    for (const child of node.children) {
      collectNodeLeaves(child, parentSelector, nextAtRules, leaves, occurrenceByBaseKey, observedRoots, warnings)
    }
    return
  } else {
    // Continue selector-node leaf collection.
  }

  const resolvedSelector = resolveSelector(parentSelector, node.value)
  // Drop selector payloads that could break out of generated style output.
  if (containsUnsafeCssPayload(resolvedSelector)) {
    return
  } else {
    // Continue normal leaf collection for safe selectors.
  }

  const observedDisabled = isObservedDisallowedAtRuleChain(atRules)
  const invalidObservedMessage = observedDisabled ? null : findInvalidObservedSelectorMessage(resolvedSelector)
  // Emit one soft warning when an observed marker falls back to static syntax.
  if (warnings && invalidObservedMessage) {
    warnings.push({
      code: 'invalid-observed-syntax',
      line: node.line ?? 1,
      message: invalidObservedMessage,
    })
  } else {
    // Continue leaf collection when selector has no invalid observed syntax.
  }

  const classifiedParts = classifyObservedSelectorParts(resolvedSelector, observedRoots, observedDisabled)
  const selector = stripObservedMarkersFromSelector(resolvedSelector)
  // Emit one leaf when selector has declaration lines.
  if (node.declarations.length > 0) {
    pushLeafGroups(classifiedParts, atRules, node, leaves, occurrenceByBaseKey, createLeafKey)
  } else {
    // Skip empty selector nodes that carry only nested children.
  }

  // Continue collecting nested selector children recursively.
  for (const child of node.children) {
    collectNodeLeaves(child, selector, atRules, leaves, occurrenceByBaseKey, observedRoots, warnings)
  }
}

// Collect explicit observed roots before selector leaves are classified.
function collectObservedRoots(root: LuisNode, warnings?: LuisCompileWarning[]): ObservedRootRegistry {
  const roots: ObservedRootRegistry = new Map()
  for (const child of root.children) {
    collectNodeObservedRoots(child, '', [], roots, warnings)
  }
  return roots
}

// Collect observed roots from one node using resolved nested selectors.
function collectNodeObservedRoots(
  node: LuisNode,
  parentSelector: string,
  atRules: string[],
  roots: ObservedRootRegistry,
  warnings?: LuisCompileWarning[],
): void {
  if (node.type === 'at-rule') {
    for (const child of node.children) {
      collectNodeObservedRoots(child, parentSelector, [...atRules, node.value], roots, warnings)
    }
    return
  }

  const resolvedSelector = resolveSelector(parentSelector, node.value)
  const observedDisabled = isObservedDisallowedAtRuleChain(atRules)
  if (!observedDisabled && !containsUnsafeCssPayload(resolvedSelector)) {
    registerObservedRootsFromSelector(resolvedSelector, roots, node.line ?? 1, warnings)
  }

  const selector = stripObservedMarkersFromSelector(resolvedSelector)
  for (const child of node.children) {
    collectNodeObservedRoots(child, selector, atRules, roots, warnings)
  }
}

// Resolve nested selector syntax and replace '&' with parent selector.
function resolveSelector(parent: string, current: string): string {
  // Keep current selector untouched for top-level rules.
  if (!parent) {
    return current
  } else if (current.includes('&')) {
    // Replace '&' placeholders with resolved parent selector.
    return current.split('&').join(parent)
  } else {
    // Return default descendant selector composition.
    return `${parent} ${current}`
  }
}

// Build stable per-leaf key from selector + at-rule chain + occurrence.
function createLeafKey(selector: string, atRules: string[], occurrenceByBaseKey: Map<string, number>): string {
  const atRulePart = atRules.join('\u001f')
  const baseKey = `${atRulePart}\u001e${selector}`
  const currentCount = occurrenceByBaseKey.get(baseKey) ?? 0
  occurrenceByBaseKey.set(baseKey, currentCount + 1)
  // Return stable key with occurrence suffix for duplicates.
  return `${baseKey}\u001e${currentCount}`
}

// Detect at-rule chains where observed selector markers are disabled.
function isObservedDisallowedAtRuleChain(atRules: string[]): boolean {
  // Scan every parent at-rule token in current selector chain.
  for (const atRule of atRules) {
    const normalized = atRule.trim().toLowerCase()
    if (
      normalized.startsWith('@keyframes')
      || normalized.startsWith('@-webkit-keyframes')
    ) {
      return true
    } else {
      // Continue scanning remaining at-rule tokens.
    }
  }

  // Return false when chain has no observed-restricted at-rules.
  return false
}

// Detect payloads that should never be emitted into generated css.
function containsUnsafeCssPayload(input: string): boolean {
  try {
    // Reuse shared guard predicate for selector safety.
    return hasUnsafeCssPayload(input)
  } catch (error) {
    // Stop compile flow when css payload sanitizer cannot execute.
    return failClosedSecurityCheck('containsUnsafeSelectorPayload', error)
  }
}
